import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Card, DatePicker, Descriptions, Empty, Input, message, Modal,
  Pagination, Select, Space, Tag, Typography,
} from 'antd';
import {
  DownloadOutlined, EyeOutlined, ReloadOutlined, SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  downloadComponentBuild,
  getComponentBuildDetail,
  getComponentBuilds,
  getComponentVersionCatalog,
  refreshComponentVersions,
} from '../services/api';
import { formatFileSize } from '../utils/format';

const { RangePicker } = DatePicker;
const { Paragraph, Text, Title } = Typography;

const DEFAULT_PAGE_SIZE = 50;

function buildDownloadName(record) {
  const safe = (value) => String(value || '').replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '');
  const parts = record.line === record.category
    ? [record.line, record.build]
    : [record.line, record.category, record.build];
  return `${parts.map(safe).filter(Boolean).join('_') || 'component-build'}.zip`;
}

function getFirstAvailableSelection(catalog = []) {
  const line = catalog.find(item => item.exists && item.categories?.length > 0);
  if (!line) return { line: undefined, category: undefined };
  return { line: line.line, category: line.categories[0]?.name };
}

function renderEntryTag(entry) {
  if (entry.type === 'directory') {
    return <Tag color="blue" key={entry.name}>{entry.name}/</Tag>;
  }
  return (
    <Tag key={entry.name}>
      {entry.name}
      {entry.size !== null && entry.size !== undefined ? ` ${formatFileSize(entry.size)}` : ''}
    </Tag>
  );
}

export default function ComponentVersions() {
  const [catalog, setCatalog] = useState([]);
  const [selectedLine, setSelectedLine] = useState();
  const [selectedCategory, setSelectedCategory] = useState();
  const [keyword, setKeyword] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [filters, setFilters] = useState({ search: '', dateRange: null });
  const [builds, setBuilds] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const selectionRef = useRef({ line: undefined, category: undefined });

  const selectedLineInfo = useMemo(
    () => catalog.find(item => item.line === selectedLine),
    [catalog, selectedLine]
  );
  const categoryOptions = useMemo(
    () => (selectedLineInfo?.categories || []).map(category => ({
      label: `${category.name} (${category.buildCount})`,
      value: category.name,
    })),
    [selectedLineInfo]
  );
  const lineOptions = useMemo(
    () => catalog
      .filter(item => item.exists)
      .map(item => ({ label: `${item.line} (${item.buildCount})`, value: item.line })),
    [catalog]
  );

  useEffect(() => {
    selectionRef.current = { line: selectedLine, category: selectedCategory };
  }, [selectedCategory, selectedLine]);

  const fetchCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const res = await getComponentVersionCatalog();
      const data = res.data.data || [];
      setCatalog(data);
      const currentSelection = selectionRef.current;
      const currentLine = data.find(item => item.line === currentSelection.line);
      if (!currentLine) {
        const first = getFirstAvailableSelection(data);
        setSelectedLine(first.line);
        setSelectedCategory(first.category);
      } else if (!currentLine.categories?.some(category => category.name === currentSelection.category)) {
        setSelectedCategory(currentLine.categories?.[0]?.name);
      }
    } catch (err) {
      message.error(err.response?.data?.message || '获取组件版本目录失败');
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const fetchBuilds = useCallback(async (page = 1, pageSize = pagination.pageSize) => {
    if (!selectedLine || !selectedCategory) {
      setBuilds([]);
      setPagination(prev => ({ ...prev, current: 1, total: 0 }));
      return;
    }

    setLoading(true);
    try {
      const range = filters.dateRange || [];
      const res = await getComponentBuilds({
        line: selectedLine,
        category: selectedCategory,
        search: filters.search || undefined,
        date_from: range[0] ? range[0].format('YYYY-MM-DD') : undefined,
        date_to: range[1] ? range[1].format('YYYY-MM-DD') : undefined,
        page,
        pageSize,
      });
      setBuilds(res.data.data || []);
      setPagination({
        current: res.data.page || page,
        pageSize: res.data.pageSize || pageSize,
        total: res.data.total || 0,
      });
    } catch (err) {
      message.error(err.response?.data?.message || '获取组件版本失败');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize, selectedCategory, selectedLine]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  useEffect(() => {
    fetchBuilds(1);
  }, [fetchBuilds]);

  const handleLineChange = (line) => {
    const nextLine = catalog.find(item => item.line === line);
    setSelectedLine(line);
    setSelectedCategory(nextLine?.categories?.[0]?.name);
  };

  const handleSearch = () => {
    setFilters({ search: keyword.trim(), dateRange });
  };

  const handleReset = () => {
    setKeyword('');
    setDateRange(null);
    setFilters({ search: '', dateRange: null });
  };

  const handleRefresh = async () => {
    setCatalogLoading(true);
    setLoading(true);
    try {
      await refreshComponentVersions();
      await fetchCatalog();
      await fetchBuilds(1, DEFAULT_PAGE_SIZE);
      message.success('组件版本已刷新');
    } catch (err) {
      message.error(err.response?.data?.message || '刷新组件版本失败');
    } finally {
      setCatalogLoading(false);
      setLoading(false);
    }
  };

  const handleDownload = async (record) => {
    try {
      await downloadComponentBuild(record.id, buildDownloadName(record));
    } catch (err) {
      message.error(err.response?.data?.message || '下载组件版本失败');
    }
  };

  const handleViewDetail = async (record) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await getComponentBuildDetail(record.id);
      setDetail(res.data.data);
    } catch (err) {
      message.error(err.response?.data?.message || '获取组件版本详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const activeLineBuildCount = selectedLineInfo?.buildCount || 0;

  return (
    <div className="page-shell">
      <div className="page-heading">
        <div>
          <div className="page-kicker">Component Builds</div>
          <Title level={3} className="page-heading-title">组件版本</Title>
          <Text className="page-heading-subtitle">查看组件构建产物、变更说明和构建附件。</Text>
        </div>
        <Space wrap>
          <Tag color="#1677ff" style={{ margin: 0 }}>{catalog.length} 条组件线</Tag>
          <Tag color="#1f9d55" style={{ margin: 0 }}>{activeLineBuildCount} 个构建</Tag>
          <Tag style={{ margin: 0 }}>最多显示 50 个</Tag>
        </Space>
      </div>

      <Card className="toolbar-card component-version-toolbar" loading={catalogLoading}>
        <div className="component-version-toolbar-row">
          <Space className="component-version-filter-group">
            <Select
              placeholder="组件线"
              className="component-version-line-select"
              value={selectedLine}
              options={lineOptions}
              onChange={handleLineChange}
            />
            <Select
              placeholder="组件分类"
              className="component-version-category-select"
              value={selectedCategory}
              options={categoryOptions}
              onChange={setSelectedCategory}
            />
            <RangePicker
              className="component-version-date-range"
              value={dateRange}
              onChange={setDateRange}
              allowClear
            />
            <Input
              placeholder="构建号 / revision / 作者 / 说明"
              prefix={<SearchOutlined />}
              allowClear
              className="component-version-search-input"
              value={keyword}
              onChange={event => setKeyword(event.target.value)}
              onPressEnter={handleSearch}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
          </Space>
          <Space className="component-version-toolbar-actions">
            <Button onClick={handleReset}>重置</Button>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              刷新
            </Button>
          </Space>
        </div>
      </Card>

      <Card className="content-card component-version-content" loading={loading}>
        {builds.length === 0 ? (
          <Empty description="暂无组件版本" />
        ) : (
          <div className="component-version-list">
            {builds.map(record => (
              <Card
                key={record.id}
                size="small"
                className="component-version-card"
              >
                <div className="component-version-main">
                  <div className="component-version-title">
                    <Text strong className="component-version-category">{record.category}</Text>
                    <Text strong className="component-version-build">{record.build}</Text>
                  </div>
                  <Space size={6} wrap>
                    <Tag color="blue">{record.line}</Tag>
                    {record.revision && <Tag color="purple">{record.revision}</Tag>}
                    {record.infoFileName && <Tag>{record.infoFileName}</Tag>}
                  </Space>
                  <Paragraph className="component-version-note">
                    {record.notePreview || record.noteText || '暂无变更说明'}
                  </Paragraph>
                </div>

                <div className="component-version-meta">
                  <div className="component-version-author-meta">
                    <span>作者</span>
                    <strong>{record.author || '-'}</strong>
                  </div>
                  <div className="component-version-time-meta">
                    <span>构建时间</span>
                    <strong>{record.buildDate ? dayjs(record.buildDate).format('YYYY-MM-DD HH:mm') : '-'}</strong>
                  </div>
                </div>

                <div className="component-version-files">
                  <Space wrap size={[4, 4]}>
                    {(record.entries || []).slice(0, 8).map(renderEntryTag)}
                    {(record.entries || []).length > 8 && <Tag>+{record.entries.length - 8}</Tag>}
                  </Space>
                </div>

                <div className="component-version-actions">
                  <Space>
                    <Button icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
                      详情
                    </Button>
                    <Button type="primary" icon={<DownloadOutlined />} onClick={() => handleDownload(record)}>
                      下载
                    </Button>
                  </Space>
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="daily-version-pagination">
          <Pagination
            {...pagination}
            showSizeChanger={false}
            showTotal={(total) => `显示最新 ${total} 个组件版本（最多 50 个）`}
            onChange={() => fetchBuilds(1, DEFAULT_PAGE_SIZE)}
          />
        </div>
      </Card>

      <Modal
        title={detail ? `${detail.category} / ${detail.build}` : '组件版本详情'}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={detail ? [
          <Button key="close" onClick={() => setDetailOpen(false)}>关闭</Button>,
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={() => handleDownload(detail)}>
            下载
          </Button>,
        ] : null}
        width={880}
        loading={detailLoading}
      >
        {detail && (
          <div className="component-version-detail">
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="组件线">{detail.line}</Descriptions.Item>
              <Descriptions.Item label="组件分类">{detail.category}</Descriptions.Item>
              <Descriptions.Item label="构建版本">{detail.build}</Descriptions.Item>
              <Descriptions.Item label="Revision">{detail.revision || '-'}</Descriptions.Item>
              <Descriptions.Item label="作者">{detail.author || '-'}</Descriptions.Item>
              <Descriptions.Item label="提交时间">{detail.commitTime || '-'}</Descriptions.Item>
              <Descriptions.Item label="更新时间">
                {detail.modifiedAt ? dayjs(detail.modifiedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="说明文件">{detail.infoFileName || '-'}</Descriptions.Item>
            </Descriptions>

            <div className="component-version-detail-section">
              <Text strong>文件</Text>
              <div className="component-version-entry-panel">
                <Space wrap size={[4, 4]}>
                  {(detail.entries || []).map(renderEntryTag)}
                </Space>
              </div>
            </div>

            <div className="component-version-detail-section">
              <Text strong>变更说明</Text>
              <pre className="component-version-note-block">
                {detail.rawNoteText || detail.noteText || '暂无变更说明'}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
